/**
 * Role-based permissions for the app.
 *
 * Each role has a list of allowed PATHS (relative to tenant base path).
 * Routes are filtered in DesktopSidebar + PlusPage + TenantRoutes based on user role.
 *
 * Usage:
 *   import { canAccess, allowedPaths } from '../utils/permissions';
 *   if (!canAccess(user?.role, '/equipe')) { ... }
 */

// Base paths everyone authenticated can always access
const COMMON_PATHS = [
  '/dashboard',      // Accueil (feed)
  '/explorer',       // Recherche globale (Fil IG)
  '/mon-compte',     // Mon resto
  '/plus',           // Plus menu
  '/profil',         // Own profile
  '/aide',           // Help center
  '/missions', '/base360', '/compte', '/compte-credits', // LOT 39-40 · droits vérifiés côté serveur
  '/feed', '/mon-parcours', '/haccp', '/haccp/passeport', '/haccp/temperatures', '/taches',
  '/role-setup', '/onboarding', '/moi', '/tablette',
  '/cuisine',        // Affichage cuisine d'une fiche (lecture seule, sans coûts) — le droit « Consulter » est vérifié côté serveur
];

export const ROLE_PERMISSIONS = {
  owner: ['*'], // full access
  communication: ['/da', '/social-media', '/dashboard', '/chat', '/profil', '/aide'], // membre com : Social media (DA & contenus) uniquement
  manager: [
    ...COMMON_PATHS,
    '/equipe', '/pointage', '/planning', '/documents', '/conges', '/formations',
    '/juridique', '/fiches', '/materiel', '/stocks', '/dlc', '/checklists',
    '/fournisseurs', '/articles-achetes', '/prix-achat', '/achats-recurrents', '/commandes-recurrentes', '/commandes', '/achats',
    '/dashboard-financier', '/caisse', '/dashboard-ca', '/rentabilite', '/finance',
    '/performance',
    '/meteo', '/chat', '/charte', '/charte-graphique', '/da', '/social-media', '/reseaux-sociaux',
    '/histoire', '/configuration', '/cockpit', '/parametres', '/lieux',
    '/developper-ca', '/actions', '/tableau-de-bord', '/connecteurs-caisse', '/integrations', '/relation-client',
    '/recettes-couts', '/communiquer', '/reservations', '/hygiene', '/recrutement',
    // No /parametres, no /facturation
  ],
  chef: [
    ...COMMON_PATHS,
    '/equipe',             // view team (read-only in UI)
    '/planning',           // view planning
    '/pointage',           // clock in/out
    '/fiches',             // recettes & coûts
    '/materiel',           // packaging et équipements
    '/stocks',             // stocks
    '/dlc',                // DLC alerts
    '/checklists',         // HACCP
    '/fournisseurs',       // fournisseurs
    '/achats-recurrents', '/commandes-recurrentes', '/articles-achetes', '/prix-achat', '/achats', '/liaisons', '/postes',
    '/commandes',
    '/formations',
    '/chat', '/meteo',
  ],
  barman: [
    ...COMMON_PATHS,
    '/planning',
    '/pointage',
    '/caisse',
    '/stocks',             // view stocks (bar side)
    '/formations',
    '/chat', '/meteo',
  ],
  // LOT 26 · Contrôleur de gestion (rôle technique consultant) : Stock, Achats, Finance seulement
  consultant: [
    ...COMMON_PATHS,
    '/finance', '/finance-hub', '/stocks', '/achats', '/factures', '/fournisseurs', '/inventaire', '/commandes', '/imports',
  ],
  serveur: [
    ...COMMON_PATHS,
    '/planning',
    '/pointage',
    '/caisse',
    '/formations',
    '/chat', '/meteo',
  ],
};

/**
 * Returns true if a user can access the given path.
 * DISABLED per user request (April 23, 2026) — any authenticated user sees everything.
 * Re-enable the role check block below to reinstate RBAC.
 */
export const canAccess = (userOrRole, path) => {
  if (!userOrRole) return false;
  const normalized = path.startsWith('/') ? path : `/${path}`;

  let role = null;
  let customPerms = null;

  if (typeof userOrRole === 'string') {
    role = userOrRole;
  } else {
    role = userOrRole.role;
    customPerms = userOrRole.custom_permissions;
  }

  // If custom_permissions is set (non-null), use it exclusively (overrides role)
  if (Array.isArray(customPerms)) {
    if (customPerms.includes('*')) return true;
    const sect = `/${normalized.split('/')[1]}`;
    return customPerms.includes(normalized) || customPerms.includes(sect) || COMMON_PATHS.includes(normalized) || COMMON_PATHS.includes(sect);
  }

  const perms = ROLE_PERMISSIONS[role === 'chef_cuisine' ? 'chef' : role] || ROLE_PERMISSIONS.serveur;
  if (perms.includes('*')) return true;
  const section = `/${normalized.split('/')[1]}`; // sous-page (/cuisine/fiche/x, /recettes-couts/plat) → droit de sa rubrique
  return perms.includes(normalized) || perms.includes(section) || COMMON_PATHS.includes(normalized) || COMMON_PATHS.includes(section);
};

/**
 * Returns the full list of paths a user has access to (for sidebar rendering).
 * DISABLED — returns ['*'] (all access) for any authenticated user.
 */
export const allowedPathsForUser = (user) => {
  if (!user) return [];
  if(Array.isArray(user.custom_permissions))return [...new Set([...COMMON_PATHS,...user.custom_permissions])];
  return ROLE_PERMISSIONS[user.role==='chef_cuisine'?'chef':user.role] || ROLE_PERMISSIONS.serveur;
};

/**
 * Returns the full list of allowed paths for this role (legacy).
 * Returns ['*'] for owner (meaning all).
 */
export const allowedPaths = (role) => {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.serveur;
};

/**
 * List of modules with user-friendly labels (for UI pickers in Equipe)
 */
export const MODULES_LIST = [
  { path: '/dashboard', label: 'Accueil (Feed)', group: 'Général', always: true },
  { path: '/planning', label: 'Planning', group: 'Général' },
  { path: '/pointage', label: 'Arrivées & Départs', group: 'Général' },
  { path: '/taches', label: 'Suivi des tâches', group: 'Général' },
  { path: '/formations', label: 'Formations', group: 'Général' },
  { path: '/chat', label: 'Parler à Sabril', group: 'Général' },
  { path: '/meteo', label: 'Météo 15 jours', group: 'Général' },

  { path: '/equipe', label: 'Équipe', group: 'RH' },

  { path: '/fiches', label: 'Recettes & Coûts', group: 'Cuisine' },
  { path: '/materiel', label: 'Packaging et équipements', group: 'Cuisine' },
  { path: '/stocks', label: 'Stocks', group: 'Cuisine' },
  { path: '/dlc', label: 'DLC / Alertes', group: 'Cuisine' },
  { path: '/checklists', label: 'Checklists HACCP', group: 'Cuisine' },

  { path: '/fournisseurs', label: 'Fournisseurs', group: 'Achats' },
  { path: '/articles-achetes', label: 'Articles achetés', group: 'Achats' },
  { path: '/prix-achat', label: 'Prix d’achat', group: 'Achats' },
  { path: '/commandes-recurrentes', label: 'Commandes récurrentes', group: 'Achats' },
  { path: '/commandes', label: 'Commandes', group: 'Achats' },

  { path: '/finance', label: 'Finance (module unifié)', group: 'Finance' },
  { path: '/facturation', label: 'Facturation', group: 'Finance' },

  { path: '/performance', label: 'Objectifs & Primes', group: 'Performance', deprecated: true },
  { path: '/evaluation', label: 'Évaluation & Primes', group: 'Performance' },
  { path: '/developper-ca', label: 'Développer chiffre', group: 'Performance' },
];
/**
 * Role display labels
 */
export const ROLE_LABELS = {
  owner: 'Patron',
  manager: 'Manager',
  chef: 'Chef',
  barman: 'Barman',
  serveur: 'Serveur',
};
